// The wire shape of one connected integration, stated once (`resource.ts` says
// why here).
//
// Two doors projected this body with BYTE-IDENTICAL return blocks: `toView`
// (`apps/api/src/routes/integrations.ts`) and `toIntegrationView`
// (`packages/mcp/src/army/integration.ts`), the second of which already carried
// the comment "mirrors `toView`" — a mirror a human had to hold, over the one
// projection whose job is to keep a credential from crossing a boundary.

import { z } from 'zod';
import { type ErrorHygienePass, ErrorHygienePassSchema } from '../entities/error-hygiene';
import {
  type IntegrationKind,
  IntegrationKindSchema,
  type IntegrationStatus,
  IntegrationStatusSchema,
} from '../entities/integration';
import { defineResource, type WireOf } from './resource';

/**
 * What the projection reads off an integration row.
 *
 * STRUCTURAL and MINIMAL, which is the point: `secretRef` — the vault pointer —
 * is not a field this type can even name, so no projection built from it can
 * leak one. The stored `Integration` (`entities/integration.ts`) satisfies it and
 * so does the MCP `IntegrationView`.
 */
export type IntegrationEntity = {
  id: string;
  kind: IntegrationKind;
  /** Non-secret connector config; the shape is validated per-kind at connect time. */
  config: Record<string, unknown>;
  status: IntegrationStatus;
  lastError: string | null;
  createdAt: Date;
  updatedAt: Date;
};

/**
 * The published body. Snake_case, ISO instants, transform-free.
 *
 * `kind` and `status` DO carry their domain enums, unlike the grant resource's:
 * both doors already typed them that way, so narrowing the schema costs nothing
 * and buys a `z.toJSONSchema` document that names the connectors a client can
 * actually receive.
 *
 * `connected_at` is `createdAt` — the row is created at connect, and the wire
 * name is the one the dashboard has always read.
 */
export const IntegrationWireSchema = z.object({
  id: z.string(),
  kind: IntegrationKindSchema,
  config: z.record(z.string(), z.unknown()),
  status: IntegrationStatusSchema,
  last_error: z.string().nullable(),
  connected_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

/**
 * The newest error-hygiene pass over the account's error tracker (`docs/idea/error-hygiene.md`):
 * the stored `error_hygiene.pass` row plus when it ran. `GET /v1/integrations` serves it
 * BESIDE the page (`hygiene_last_pass`, like `oauth_kinds`), never on a row: the integration
 * body is ONE body across REST and MCP (`contract-body-parity.test.ts`), and a pass is a fact
 * about the account's tracker, not a field of the connection row. `null`: no pass has run, no
 * `sentry` connection, or the stored row no longer parses.
 */
export const HygieneLastPassWireSchema = ErrorHygienePassSchema.extend({
  ran_at: z.iso.datetime(),
});

/** The one integration projection. Every door calls this rather than re-deriving it. */
export const IntegrationResource = defineResource<IntegrationEntity, typeof IntegrationWireSchema>({
  schema: IntegrationWireSchema,
  serialize: (row) => ({
    id: row.id,
    kind: row.kind,
    config: row.config,
    status: row.status,
    last_error: row.lastError,
    connected_at: row.createdAt.toISOString(),
    updated_at: row.updatedAt.toISOString(),
  }),
});

/** The `hygiene_last_pass` body: the stored pass and when it ran. */
export function hygieneLastPassWire(
  ranAt: Date,
  pass: ErrorHygienePass,
): ErrorHygienePass & { ran_at: string } {
  return { ...pass, ran_at: ranAt.toISOString() };
}

/** What a client should type an integration body against. */
export type IntegrationWire = WireOf<typeof IntegrationResource>;
